import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import { MatSidenav } from '@angular/material/sidenav';

import { ClassesEntity } from '../../../../models/entities/classes-entity';
import { IClassesEntityService } from '../../../../interfaces/entities/classes-entity-service.interface';
import { CLASSES_ENTITY_SERVICE_TOKEN } from '../../../../injection-tokens/entity-service-tokens';
import { UserEntityService } from '../../../../services/settings/user-entity.service';
import { RightsCases } from '../../../../models/rights-cases-enum';
import { DrawerPayload, DrawerService } from '../../../../services/drawer.service';

/** Supported tab index constants for query-param-driven navigation. */
const TAB = { DETAILS: 0, ACTIVITY: 1, PARTICIPANTS: 2, SETTINGS: 3, RESOURCES: 4, COST_ANALYSIS: 5 } as const;
const TAB_PARAM_MAP: Record<string, number> = {
  details: TAB.DETAILS,
  activity: TAB.ACTIVITY,
  participants: TAB.PARTICIPANTS,
  settings: TAB.SETTINGS,
  resources: TAB.RESOURCES,
  cost_analysis: TAB.COST_ANALYSIS,
};
const TAB_INDEX_MAP: Record<number, string> = {
  [TAB.DETAILS]: 'details',
  [TAB.ACTIVITY]: 'activity',
  [TAB.PARTICIPANTS]: 'participants',
  [TAB.SETTINGS]: 'settings',
  [TAB.RESOURCES]: 'resources',
  [TAB.COST_ANALYSIS]: 'cost_analysis',
};

@Component({
  selector: 'app-class-detail-v2',
  templateUrl: './class-detail-v2.component.html',
  styleUrls: ['./class-detail-v2.component.css'],
})
export class ClassDetailV2Component implements OnInit, OnDestroy {
  classId!: string;
  classEntity: ClassesEntity | null = null;
  isLoading = true;
  loadError: string | null = null;

  /** Reference to the page-level installation status drawer. */
  @ViewChild('cdDrawer', { static: true }) private readonly cdDrawer!: MatSidenav;

  /** Currently displayed drawer payload; null before first open. */
  drawerPayload: DrawerPayload | null = null;

  /** Composite subscription for drawer open/close streams — cleaned up on destroy. */
  private readonly drawerSubs = new Subscription();

  canAccessSettings = false;
  selectedTabIndex: number = TAB.DETAILS;
  /** Drives cross-tab deep-link navigation into a specific Settings sub-section.
   * Set by handleGoToSettingsParticipants(); cleared once SettingsTabComponent consumes it. */
  settingsInitialSection: string | null = null;

  /** Holds a tab index that requires rights to be resolved before it can be applied.
   * Set when deep-linking to Settings/Resources before checkRights() has returned. */
  private pendingTabIndex: number | null = null;

  /** Resource type flags derived from the class's resourceTemplateIds. */
  hasVmResource = false;
  hasAccResource = false;

  /** True once the VM template for this class is confirmed provisioned. */
  isVmTemplateReady = false;
  /** True once a snapshot has been created for the VM template (Testing Done). */
  hasSnapshot = false;
  /** True when the current user has a trainer/instructor/faculty role. */
  get isTrainerRole(): boolean {
    return ['trainer', 'instructor', 'faculty'].includes(this.userRole.toLowerCase());
  }

  /** False for student/participant roles — hides Activity and Participants tabs and prevents their API calls. */
  get canViewActivityAndParticipants(): boolean {
    return !['student', 'participant'].includes(this.userRole.toLowerCase());
  }

  private readonly destroy$ = new Subject<void>();

  readonly userId = localStorage.getItem('userId') ?? '';
  readonly userName = localStorage.getItem('userName') ?? '';

  /**
   * Resolved from the 'roles' JSON array (e.g. ["admin","staff","trainer"]),
   * same source used by the sidebar and rights guard.
   * Falls back to 'student' only when the array is empty or unparseable,
   * so a missing/null 'userRole' key can never accidentally hide tabs for admin.
   */
  readonly userRole: string = (() => {
    try {
      const raw = localStorage.getItem('roles');
      if (raw) {
        const arr: string[] = JSON.parse(raw);
        if (arr?.length) return arr[0].toLowerCase();
      }
    } catch { /* ignore */ }
    return 'student';
  })();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    @Inject(CLASSES_ENTITY_SERVICE_TOKEN) private readonly classService: IClassesEntityService,
    private readonly userEntityService: UserEntityService,
    protected readonly drawerService: DrawerService,
  ) { }

  ngOnInit(): void {
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      // Defensively strip any leaked query-string suffix from the path segment.
      this.classId = (params['classId'] ?? '').split('?')[0];
      this.loadClass();
    });

    // Read the tab query-param exactly once on initial load, then wipe it from the URL.
    // Using first() ensures this auto-completes and never re-triggers on subsequent navigations.
    this.route.queryParams.pipe(first()).subscribe(qp => {
      const tabName = qp['tab'] as string | undefined;
      if (tabName && tabName in TAB_PARAM_MAP) {
        const idx = TAB_PARAM_MAP[tabName];
        // Settings and Resources tabs are hidden until checkRights() resolves.
        // Store the index so checkRights() can apply it once the tabs are visible.
        if (idx >= TAB.SETTINGS) {
          this.pendingTabIndex = idx;
        } else {
          this.selectedTabIndex = idx;
        }
      }
      // Remove the tab param from the URL immediately to prevent it from being re-encoded
      // into the path segment on hash-routing navigations.
      if (tabName) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {},
          replaceUrl: true,
        });
      }
    });

    this.checkRights();

    // Wire the page-level installation-status drawer.
    this.drawerSubs.add(
      this.drawerService.open$.subscribe(payload => {
        this.drawerPayload = payload;
        this.cdDrawer.open();
      })
    );
    this.drawerSubs.add(
      this.drawerService.close$.subscribe(() => this.cdDrawer.close())
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.drawerSubs.unsubscribe();
  }

  private loadClass(silent = false): void {
    if (!silent) {
      this.isLoading = true;
      this.loadError = null;
    }

    this.classService.getClassByClassId(this.classId).subscribe({
      next: (entity: ClassesEntity) => {
        this.classEntity = entity;
        const templates: any[] = entity?.trainingSchedule?.resourceTemplateIds ?? [];
        const typeFn = (r: any) => (r?.resourceType ?? r?.type ?? r?.ResourceType ?? '').toLowerCase();
        this.hasVmResource = templates.some(r => typeFn(r) === 'vm');
        this.hasAccResource = templates.some(r => typeFn(r) === 'acc');
        if (!silent) this.isLoading = false;

        // Check template provisioning status for VM resources (used by Launch Lab visibility).
        if (this.hasVmResource) {
          const vmTemplate = templates.find(r => typeFn(r) === 'vm');
          const templateId = vmTemplate?.id ?? vmTemplate?.templateId ?? vmTemplate?.Id;
          if (templateId) {
            this.checkTemplateStatus(templateId);
          }
        }
      },
      error: (err: any) => {
        if (!silent) {
          this.loadError = err?.error?.message ?? 'Failed to load class details.';
          this.isLoading = false;
        }
      },
    });
  }

  /** Re-fetches the class entity in the background without showing the full-page spinner.
   * Called after VM subscriptions are added so the VM-template guard re-evaluates. */
  reloadClassSilent(): void {
    this.loadClass(true);
  }

  private checkTemplateStatus(templateId: string): void {
    const customerId = (this.classEntity as any)?.customer?.id ?? '';
    this.classService.getTemplateStatus(templateId, customerId, this.classId).subscribe({
      next: res => {
        this.isVmTemplateReady = res?.isProvisioned === true;
        // hasSnapshot is true only when the Azure snapshot actually exists right now.
        // classStage='confirm' means the class was once confirmed but the snapshot may have
        // been externally deleted — the liveness probe in GetTemplateStatus clears stale
        // state and returns status='draft' in that case.
        this.hasSnapshot = (res?.status ?? '').toLowerCase() === 'completed';
      },
      error: () => {
        this.isVmTemplateReady = false;
        this.hasSnapshot = false;
      },
    });
  }

  private checkRights(): void {
    if (!this.userId) return;

    this.userEntityService
      .rightsCasesGetByUserId(this.userId, [RightsCases.cloudSettings])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rights: any) => {
          this.canAccessSettings = rights?.cloudSettings === true;
          // If a rights-gated tab was deep-linked (e.g. from a notification), apply it now
          // that the tab is visible. Use Promise to defer until the *ngIf tabs are in the DOM.
          if (this.pendingTabIndex !== null) {
            const idx = this.pendingTabIndex;
            this.pendingTabIndex = null;
            Promise.resolve().then(() => { this.selectedTabIndex = idx; });
          }
        },
        error: () => {
          this.canAccessSettings = false;
          this.pendingTabIndex = null;
        },
      });
  }

  onTabChange(index: number): void {
    this.selectedTabIndex = index;
    // Intentionally not writing tab name back to the URL.
    // Writing query params on every tab click was the root cause of the ?tab= → classId pollution.
  }

  /** Emitted from the Roster Sheet tab empty states — navigates to Settings → Participants. */
  handleGoToSettingsParticipants(): void {
    if (!this.canAccessSettings) return;
    this.selectedTabIndex = TAB.SETTINGS;
    this.settingsInitialSection = 'participants';
  }

  /** Emitted from the Activity tab empty state when the VM template isn't ready — navigates to Settings → VM Template. */
  handleGoToSettingsVMTemplate(): void {
    if (!this.canAccessSettings) return;
    this.selectedTabIndex = TAB.SETTINGS;
    this.settingsInitialSection = 'vm-template';
  }

  goBack(): void {
    const fromParam = this.route.snapshot.params['from'];
    if (fromParam === 'notifications') {
      this.router.navigate(['/notifications']);
    } else {
      this.router.navigate(['/classes']);
    }
  }

  // ── Drawer status helpers ──────────────────────────────────────────────────

  /** Closes the installation-status drawer via DrawerService. */
  closeDrawer(): void {
    this.drawerService.close();
  }
}
